/**
 * PublicSalesDeckPage — no-auth shareable presentation viewer.
 *
 * Lives at /p/deck/:typeId. The Country Operator's sales team copies this URL
 * from the SalesDeckPage Share dialog and sends it to a prospect via
 * WhatsApp/Email/SMS. The page resolves the current hostname against
 * /api/whitelabel/public/by-host so the prospect sees the Operator's brand
 * (logo, name, primary color, custom domain) in the header and footer.
 *
 * Includes a discreet "Powered by Konnect" attribution unless the operator
 * has paid for the whitelabel "hide_powered_by" tier.
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, Loader2, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { SlideCanvas } from '../components/sales-deck/SlideCanvas';

const API = process.env.REACT_APP_BACKEND_URL;

export const PublicSalesDeckPage = () => {
  const { typeId: paramTypeId } = useParams();
  const navigate = useNavigate();

  const [typeId, setTypeId] = useState(paramTypeId || '');
  const [types, setTypes] = useState([]);
  const [deck, setDeck] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [brand, setBrand] = useState(null);

  // Resolve current hostname → operator brand
  useEffect(() => {
    const host = window.location.host;
    axios
      .get(`${API}/api/whitelabel/public/by-host`, { params: { host } })
      .then((r) => {
        if (r.data?.matched) setBrand(r.data.brand);
        else setBrand({});
      })
      .catch(() => setBrand({}));
  }, []);

  // Types list (public)
  useEffect(() => {
    axios.get(`${API}/api/sales-deck/public/types`)
      .then((r) => setTypes(r.data.types || []))
      .catch(() => { /* silent */ });
  }, []);

  // Load deck whenever typeId changes (and keep URL in sync)
  useEffect(() => {
    if (!typeId) { setDeck(null); return; }
    if (paramTypeId !== typeId) navigate(`/p/deck/${typeId}`, { replace: true });
    setLoading(true);
    setSlideIdx(0);
    axios.get(`${API}/api/sales-deck/public/${typeId}`)
      .then((r) => setDeck(r.data))
      .catch(() => setDeck(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId]);

  // Apply brand colors + favicon to the public page so prospects see the
  // operator's branding in the tab title and bookmark icon too.
  useEffect(() => {
    if (brand?.primary_color) {
      document.documentElement.style.setProperty('--brand-primary', brand.primary_color);
    }
    if (brand?.company_name) {
      document.title = `${brand.company_name} — built for your business`;
    }
    const favicon = brand?.favicon_url || brand?.logo_url;
    if (favicon) {
      document.querySelectorAll(
        "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']",
      ).forEach((el) => el.parentNode.removeChild(el));
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = favicon;
      document.head.appendChild(link);
      const apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      apple.href = favicon;
      document.head.appendChild(apple);
    }
  }, [brand]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (!deck) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setSlideIdx(i => Math.min(i + 1, deck.slides.length - 1));
      }
      if (e.key === 'ArrowLeft') setSlideIdx(i => Math.max(i - 1, 0));
      if (e.key === 'Escape' && fullscreen) setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [deck, fullscreen]);

  const slideBrand = useMemo(() => ({
    name: brand?.company_name || 'Konnect',
    domain: window.location.host,
    accent: brand?.primary_color || '#10b981',
  }), [brand]);

  // Lead capture handler — only used on the CTA slide.
  const submitLead = async (payload) => {
    try {
      await axios.post(`${API}/api/sales-deck/public/lead`, {
        ...payload,
        host: window.location.host,
        type_id: typeId,
      });
      toast.success('Got it! We\'ll be in touch shortly.');
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not send. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not send. Please try again.');
      return false;
    }
  };

  const primary = brand?.primary_color || '#10b981';
  const hidePowered = !!brand?.hide_powered_by;

  return (
    <div className={fullscreen ? 'fixed inset-0 z-50 bg-white overflow-auto' : 'min-h-screen bg-off-white'}>
      {/* Branded header */}
      {!fullscreen && (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {brand?.logo_url ? (
                <img src={brand.logo_url} alt={brand.company_name || 'Logo'} className="h-9 w-auto object-contain" />
              ) : (
                <div className="text-lg font-heading font-bold text-text-primary truncate">
                  {brand?.company_name || 'Konnect'}
                </div>
              )}
              <span className="hidden sm:inline text-xs text-gray-medium border-l border-gray-200 pl-3">
                Tailored business proposal
              </span>
            </div>
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger className="w-[230px] sm:w-[280px]" data-testid="public-deck-type-select">
                <SelectValue placeholder="Pick your business type…" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {types.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.emoji} {t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </header>
      )}

      <main className={fullscreen ? 'p-6' : 'max-w-6xl mx-auto p-4 sm:p-6 space-y-4'}>
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: primary }} />
          </div>
        )}

        {!loading && !deck && (
          <div className="bg-white border border-dashed rounded-xl text-center py-20 px-6">
            <Sparkles className="h-12 w-12 mx-auto mb-3" style={{ color: primary }} />
            <h1 className="text-2xl font-heading font-bold mb-2">
              Welcome to {brand?.company_name || 'Konnect'}
            </h1>
            <p className="text-sm text-gray-medium max-w-md mx-auto">
              Pick your business type above and we'll show you a tailored 10-slide overview
              of how {brand?.company_name || 'Konnect'} fits your operation — pain points,
              modules, ROI, and a recommended plan.
            </p>
          </div>
        )}

        {deck && !loading && (
          <>
            <SlideCanvas
              slide={deck.slides[slideIdx]}
              deckLabel={deck.label}
              brand={slideBrand}
              leadCapture={{
                bookingUrl: brand?.booking_url || null,
                onSubmit: submitLead,
              }}
            />

            <div className="flex items-center justify-between bg-white rounded-lg border p-3 shadow-sm">
              <div className="text-sm text-gray-medium">
                Slide <strong>{slideIdx + 1}</strong> / {deck.slides.length}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setSlideIdx(i => Math.max(i - 1, 0))}
                  disabled={slideIdx === 0}
                  data-testid="public-deck-prev"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setFullscreen(!fullscreen)}
                  data-testid="public-deck-fullscreen"
                >
                  {fullscreen
                    ? <><Minimize2 className="h-4 w-4 mr-1" /> Exit</>
                    : <><Maximize2 className="h-4 w-4 mr-1" /> Present</>}
                </Button>
                <Button
                  size="sm"
                  className="text-white"
                  style={{ backgroundColor: primary }}
                  onClick={() => setSlideIdx(i => Math.min(i + 1, deck.slides.length - 1))}
                  disabled={slideIdx === deck.slides.length - 1}
                  data-testid="public-deck-next"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>

            {!fullscreen && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {deck.slides.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setSlideIdx(i)}
                    className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      i === slideIdx ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={i === slideIdx ? { backgroundColor: primary } : undefined}
                    data-testid={`public-deck-jump-${i}`}
                  >
                    {i + 1}. {s.title?.slice(0, 32) || s.kind}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {!fullscreen && !hidePowered && (
        <footer className="text-center py-6 text-xs text-gray-400">
          Powered by <a href="https://konnect.kaharian.com" target="_blank" rel="noopener noreferrer" className="hover:underline">Konnect</a>
        </footer>
      )}
    </div>
  );
};

export default PublicSalesDeckPage;
